import type { RaceRecording, ReplayManifest, SessionSummary } from '../../shared/race';
import { request } from '../api';

export type OpeningStage = 'metadata' | 'data';

/** Preserve the recovery action while keeping provider and infrastructure details out of the UI. */
export function replayErrorMessage(error: unknown): string {
  const message = typeof error === 'string' ? error : error instanceof Error ? error.message : '';
  if (/获取赛事信息超时/.test(message)) return '获取赛事信息超时，请重试或选择其他场次。';
  if (/首段回放数据加载超时/.test(message)) return '首段回放数据加载超时，请重试或选择其他场次。';
  if (/首次回放加载超时/.test(message)) return '首次回放加载超时，请重试或选择其他场次。';
  if (/超时|timeout|timed.?out|deadline|ETIMEDOUT/i.test(message)) return '回放数据准备超时，请重试或选择其他场次。';
  if (/429|限流|请求[过较]多|too many|rate.?limit/i.test(message)) return '数据请求较多，请稍后重试。';
  if (/连接|网络|network|connect|fetch failed|ECONN|ENOTFOUND|EAI_AGAIN/i.test(message)) return '暂时无法连接数据服务，请稍后重试。';
  if (/404|暂无|未找到|not found|no data|没有可用/i.test(message)) return '该场次暂时没有可用回放，请选择其他场次。';
  if (/502|503|504|unavailable/i.test(message)) return '数据服务暂时不可用，请稍后重试。';
  return message && !/openf1|live\s?lite|mongo|postgres|raw|mqtt|token|credential|secret|password|cookie|authorization|凭据|采集|https?:\/\//i.test(message)
    ? message : '赛事数据暂时不可用，请重试或选择其他场次。';
}

export async function fetchChunk(manifest: ReplayManifest, index: number, signal?: AbortSignal) {
  const result = await request<RaceRecording>(`/api/recordings/${encodeURIComponent(manifest.session.key)}?offsetSeconds=${index * manifest.chunkDurationMs / 1000}&durationSeconds=${manifest.chunkDurationMs / 1000}`, signal);
  signal?.throwIfAborted();
  if (result?.schemaVersion !== 1 || result.session?.key !== manifest.session.key || !Array.isArray(result.events) || !Array.isArray(result.drivers)) throw new Error('回放数据格式不受支持。');
  return result;
}

async function withDeadline<T>(signal: AbortSignal, timeoutMs: number, message: string, load: (active: AbortSignal) => Promise<T>) {
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort('timeout'), timeoutMs);
  const active = AbortSignal.any([signal, deadline.signal]);
  try {
    active.throwIfAborted();
    const value = await load(active);
    active.throwIfAborted();
    return value;
  } catch (error) {
    if (deadline.signal.aborted && !signal.aborted) throw new Error(message);
    throw error;
  } finally { clearTimeout(timer); }
}

/** A selected title is provisional until both metadata and a matching first window arrive. */
export async function loadReplayStart(key: string, signal: AbortSignal, onStage: (stage: OpeningStage, session?: SessionSummary) => void) {
  return withDeadline(signal, 120_000, '首次回放加载超时，请重试或选择其他场次。', async overall => {
    onStage('metadata');
    const manifest = await withDeadline(overall, 45_000, '获取赛事信息超时，请重试或选择其他场次。', active => request<ReplayManifest>(`/api/replays/${encodeURIComponent(key)}`, active));
    overall.throwIfAborted();
    if (typeof manifest?.session?.key !== 'string' || !manifest.session.key || (key !== 'latest' && manifest.session.key !== key)
      || !Number.isFinite(manifest.durationMs) || !(manifest.durationMs > 0) || manifest.chunkDurationMs !== 600_000) throw new Error('赛事回放信息无效，请重试。');
    onStage('data', manifest.session);
    const first = await withDeadline(overall, 90_000, '首段回放数据加载超时，请重试或选择其他场次。', active => fetchChunk(manifest, 0, active));
    overall.throwIfAborted();
    return { manifest, first };
  });
}
